import { IBaseComponentProps, NSButtonOnClickProps, NSTabPageTab } from "namirasoft-site-react";
import { NSSection, NSPanel, NSButtonBlue, NSLine, NSSpace, NSSpaceSizeType } from 'namirasoft-site-react';
import { NSARouterMakerProps } from "../NSARouterMakerProps";
import { forwardRef, ReactNode, useImperativeHandle, useRef } from "react";
import { IEntityInfo } from "../IEntityInfo";
import { NamingConvention } from "namirasoft-core";
import { NSASectionTabs, NSASectionTabsItemsProps, NSASectionTabsRef } from "./NSASectionTabs";
import { NSASectionEditTabPage } from "./NSASectionEditTabPage";
import { CategoryRow, FieldRow } from "namirasoft-field";
import { useParams } from "react-router-dom";

export interface NSASectionEditProps<EntityType extends { id: string }, EntityTypeInput = EntityType> extends NSARouterMakerProps, IBaseComponentProps
{
	isEdit: boolean;
	ui: {
		getEntity: (entity: EntityType | null) => EntityTypeInput;
		setEntity: (entity: EntityType) => void;
		action?: {
			menus: {
				apply?: boolean,
			}
		},
		tabs?: NSASectionTabsItemsProps;
		panel?: IBaseComponentProps;
		tabPage?: IBaseComponentProps;
		mainTab?: IBaseComponentProps;
		extraTabs?: NSTabPageTab[];
	}
	onEntityLoaded?: (item: EntityType) => void;
}

interface NSASectionEditInnerProps<EntityType extends { id: string }, EntityTypeInput = EntityType> extends NSASectionEditProps<EntityType, EntityTypeInput>, NSARouterMakerProps
{
	entity: IEntityInfo<EntityType, EntityTypeInput>,
	ctf: {
		enabled: boolean;
		fields: FieldRow[] | null;
		categories: CategoryRow[] | null;
	};
	deleteFieldCache: () => void;
	children: ReactNode;
}

export interface NSASectionEditRef<EntityType>
{
	NSASectionTabs: NSASectionTabsRef | null;
	onApply: NSButtonOnClickProps;
	getLoadedEntity: () => EntityType | null;
}

export const NSASectionEdit = forwardRef<any, any>(<EntityType extends { id: string }, EntityTypeInput = EntityType>(
	props: Omit<NSASectionEditInnerProps<EntityType, EntityTypeInput>, "ref">,
	ref: React.Ref<NSASectionEditRef<EntityType>>
) =>
{
	let { id } = useParams();
	let NSASectionTabs_Ref = useRef<NSASectionTabsRef>(null);
	let NSASectionEditTabPage_Ref = useRef<NSASectionEditTabPage<EntityType, EntityTypeInput>>(null);

	useImperativeHandle(ref, () => ({
		NSASectionTabs: NSASectionTabs_Ref.current,
		onApply: {
			action: (onFinished: () => void) =>
			{
				onApply(props, props.entity, id, onFinished);
			}
		},
		getLoadedEntity: () =>
		{
			return NSASectionEditTabPage_Ref.current?.getLoadedEntity() ?? null;
		}
	}));

	function onApply<EntityType extends { id: string }, EntityTypeInput = EntityType>(
		props: NSASectionEditProps<EntityType, EntityTypeInput>,
		entity: IEntityInfo<EntityType, EntityTypeInput>,
		id: string | undefined,
		onFinshied: () => void
	): void
	{
		try
		{
			let Name_Pascal_Space = NamingConvention.lower_case_underscore.convert(entity.name, NamingConvention.Pascal_Case_Space);

			let onSuccess = (new_entity: EntityType, message: string) =>
			{
				props.notifier.onSuccess(message);
				if (entity.client.onSuccess)
					entity.client.onSuccess(new_entity);
				else
				{
					let url = null;
					if (entity.client.getViewURL)
						url = entity.client.getViewURL(new_entity.id);
					else if (entity.client.getListURL)
						url = entity.client.getListURL();
					if (url)
						props.url.redirect(url, {});
				}
			};
			let cur_entity: EntityTypeInput | null = NSASectionEditTabPage_Ref.current?.getEntity() as EntityTypeInput | null;
			if (cur_entity)
				if (props.isEdit)
				{
					if (id)
					{
						if (entity.client.onUpdate)
						{
							entity.client.onUpdate(id, cur_entity).then(new_entity =>
							{
								if (new_entity)
									onSuccess(new_entity, Name_Pascal_Space + " has been updated successfully");
								onFinshied();
							}).catch(onFinshied);
						}
						else if (entity.server.update)
						{
							entity.server.update(id, cur_entity).then(new_entity =>
							{
								onSuccess(new_entity, Name_Pascal_Space + " has been updated successfully");
								onFinshied();
							}).catch(onFinshied);
						}
						else
							onFinshied();
					}
					else
						throw new Error("ID was not provided.");
				}
				else
				{
					if (entity.client.onCreate)
					{
						entity.client.onCreate(cur_entity).then(new_entity =>
						{
							if (new_entity)
								onSuccess(new_entity, Name_Pascal_Space + " has been created successfully");
							onFinshied();
						}).catch(onFinshied);
					}
					else if (entity.server.create)
					{
						entity.server.create(cur_entity).then(new_entity =>
						{
							onSuccess(new_entity, Name_Pascal_Space + " has been created successfully");;
							onFinshied();
						}).catch(onFinshied);
					}
					else
						onFinshied();
				}
			else
				onFinshied();
		} catch (error: any)
		{
			props.notifier.onError(error);
			onFinshied();
		}
	}

	return (
		<>
			<NSSection
				id={props.id}
				classList={[`${props.classList?.join(" ") ?? ""}`]}
				style={{ ...props.style }}
			>
				<NSPanel
					id={props.ui.panel?.id}
					classList={[`${props.ui.panel?.classList?.join(" ") ?? ""}`]}
					style={{ ...props.ui.panel?.style }}
				>
					<NSASectionEditTabPage
						ref={NSASectionEditTabPage_Ref}
						account={props.account}
						notifier={props.notifier}
						url={props.url}
						id={id}
						entity={props.entity}
						getEntity={props.ui.getEntity}
						setEntity={props.ui.setEntity}
						onEntityLoaded={props.onEntityLoaded}
						ctf={props.ctf}
						tabPage={props.ui.tabPage}
						mainTab={props.ui.mainTab}
						extraTabs={props.ui.extraTabs}
						deleteFieldCache={props.deleteFieldCache}
					>
						{props.children}
					</NSASectionEditTabPage>
				</NSPanel>
				<NSSpace size={NSSpaceSizeType.SMALL}></NSSpace>
				<NSButtonBlue
					title="Apply"
					onClick={{
						action: (onFinished) =>
						{
							onApply(props, props.entity, id, onFinished);
						}
					}}
				/>
			</NSSection>
			{
				props.isEdit && (props.ui.tabs?.enabled ?? true) &&
				<>
					<NSSpace size={NSSpaceSizeType.SMALL} />
					<NSLine />
					<NSSpace size={NSSpaceSizeType.SMALL} />
					<NSASectionTabs
						ref={NSASectionTabs_Ref}
						id={id ?? ""}
						{...props}
						tabs={props.ui.tabs}
					/>
				</>
			}
		</>
	);
});